'use client';

import React from 'react';
import { JsonLdScript } from './JsonLdScript';

/**
 * JSON-LD DefinedTermSet schema for glossary pages.
 * Describes a collection of defined terms for search engines and AI models.
 *
 * @see https://schema.org/DefinedTermSet
 * @see https://schema.org/DefinedTerm
 */

export interface DefinedTermInput {
  /** Kebab-case id used as URL fragment */
  id: string;
  /** Display name of the term */
  name: string;
  /** 2-3 sentence definition */
  description: string;
  /** Optional URL to a more detailed page */
  url?: string;
}

interface DefinedTermSetSchemaProps {
  /** Name of the term set (e.g., "Healthcare SEO & AEO Glossary") */
  name: string;
  /** Canonical URL of the glossary page */
  url: string;
  /** Array of terms to include */
  terms: DefinedTermInput[];
}

export function DefinedTermSetSchema({
  name,
  url,
  terms,
}: DefinedTermSetSchemaProps): React.ReactElement | null {
  if (!terms || terms.length === 0) {
    return null;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name,
    url,
    hasDefinedTerm: terms.map((term) => ({
      '@type': 'DefinedTerm',
      '@id': `${url}#${term.id}`,
      name: term.name,
      description: term.description,
      ...(term.url ? { url: term.url } : {}),
    })),
  };

  return <JsonLdScript schema={schema} />;
}
